import { CloudUpload, FileImage, FileSpreadsheet, FileText, X } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

/**
 * O campo de arquivo da casa (2026-10-14): uma área de soltar com o
 * mesmo desenho da de Uploads, no lugar do "Choose File / No file chosen"
 * nativo — que não aceita estilo, fala inglês e não diz o que cabe ali.
 *
 * Por baixo continua um `<input type="file">` de verdade (escondido só
 * visualmente): o teclado chega nele, o leitor de tela lê o rótulo
 * (`aria-labelledby` aponta pro título do campo) e os testes que acham o
 * campo pelo rótulo ou pelo `accept` seguem achando.
 *
 * Controlado: o pai guarda os arquivos (`files`) e recebe a lista nova
 * em `onChange`. Com `multiple`, escolher de novo ACRESCENTA (sem repetir
 * nome+tamanho) até `maxFiles`; sem `multiple`, troca.
 */
export function FileDrop({
  label,
  required = false,
  accept,
  multiple = false,
  maxFiles,
  files,
  onChange,
  formats,
  help,
  kind = "sheet",
  disabled = false,
  className
}: {
  label?: ReactNode;
  required?: boolean;
  accept: string;
  multiple?: boolean;
  maxFiles?: number;
  files: File[];
  onChange: (files: File[]) => void;
  /** A linha curta dos formatos: "XLSX, até 5 MB". */
  formats: string;
  /** A explicação embaixo do campo. */
  help?: ReactNode;
  kind?: "sheet" | "image" | "text";
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const Icon = kind === "image" ? FileImage : kind === "text" ? FileText : FileSpreadsheet;

  const take = (incoming: File[]) => {
    const accepted = incoming.filter(file => matchesAccept(file, accept));
    setRejected(incoming.filter(file => !accepted.includes(file)).map(file => file.name));
    if (accepted.length === 0) return;
    if (!multiple) {
      onChange([accepted[0]]);
      return;
    }
    const merged = [...files];
    for (const file of accepted) {
      if (!merged.some(known => known.name === file.name && known.size === file.size)) merged.push(file);
    }
    onChange(maxFiles ? merged.slice(0, maxFiles) : merged);
  };

  const full = multiple && maxFiles !== undefined && files.length >= maxFiles;
  const title = files.length === 0
    ? multiple ? "Arraste os arquivos aqui ou clique para escolher" : "Arraste o arquivo aqui ou clique para escolher"
    : multiple ? full ? `Limite de ${maxFiles} arquivos atingido` : "Adicionar mais arquivos" : "Trocar arquivo";

  return (
    <div className={`file-drop-field${className ? ` ${className}` : ""}`}>
      {label && (
        <span className="file-drop-label" id={`${id}-label`}>
          {label}
          {required && <b aria-hidden="true"> *</b>}
        </span>
      )}
      <label
        className={`file-drop${dragging ? " is-dragging" : ""}${files.length ? " has-files" : ""}${disabled || full ? " is-disabled" : ""}`}
        onDragOver={event => {
          event.preventDefault();
          if (!disabled && !full) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={event => {
          event.preventDefault();
          setDragging(false);
          if (!disabled && !full) take(Array.from(event.dataTransfer.files));
        }}
      >
        <input
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          disabled={disabled || full}
          aria-labelledby={label ? `${id}-label` : undefined}
          aria-describedby={`${id}-formats`}
          onChange={event => {
            take(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
        <span className="file-drop-icon" aria-hidden="true">
          <CloudUpload size={20} />
        </span>
        <span className="file-drop-copy">
          <strong>{title}</strong>
          <small id={`${id}-formats`}>{formats}</small>
        </span>
      </label>

      {rejected.length > 0 && (
        <p className="file-drop-error" role="alert">
          {rejected.length === 1 ? `"${rejected[0]}" não é` : `${rejected.length} arquivos não são`} de um
          formato aceito aqui ({formats}).
        </p>
      )}

      {files.length > 0 && (
        <ul className="file-drop-list">
          {files.map(file => (
            <li key={`${file.name}-${file.size}`}>
              <Icon size={16} aria-hidden="true" />
              <span className="file-drop-name">{file.name}</span>
              <span className="file-drop-size">{fileSizeLabel(file.size)}</span>
              <button
                type="button"
                className="icon-button"
                aria-label={`Remover ${file.name}`}
                disabled={disabled}
                onClick={() => onChange(files.filter(item => item !== file))}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {help && <small className="file-drop-help">{help}</small>}
    </div>
  );
}

/** "12 KB", "3,4 MB". */
export function fileSizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/**
 * O arquivo casa com o `accept` do campo? A mesma regra do navegador:
 * extensão (`.xlsx`), tipo exato (`image/png`) ou família (`image/*`).
 * Quem escolhe pela janela do sistema já vem filtrado; quem ARRASTA não,
 * e é pra isso que esta checagem existe.
 */
export function matchesAccept(file: File, accept: string): boolean {
  const tokens = accept.split(",").map(token => token.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return tokens.some(token => {
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}
